import { Component, inject, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { NgClass } from '@angular/common';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { Helpers } from '../../../../../utilities/helpers';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { ISchools } from '../../../../../interfaces/school';
import { FlashService } from '../../../../../services/flash';
import { ILevel, ILevels } from '../../../../../interfaces/level';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { ActivatedRoute, Router } from '@angular/router';
import { Session } from '../../../../../services/session';
import { LevelService } from '../../../../../services/level';
import { SchoolService } from '../../../../../services/school';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';

@Component({
  selector: 'app-levels',
  imports: [
    FormsModule,
    Pagination,
    NgClass,
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    ShimmerEffect,
  ],
  templateUrl: './levels.html',
  styleUrl: './levels.css',
})
export class Levels {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private session: Session,
    private levelService: LevelService,
    private schoolService: SchoolService,
  ) {
      this.isLoading.set(true);

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role ?? '';
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getLevels();
  }

  protected readonly Helpers = Helpers;
  protected isFiltering = signal<boolean>(false);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected search: string = '';
  protected filterSchoolId: string = '';
  protected schoolId?: string;
  protected loggedUserRole: string = '';
  protected levels = signal<ILevels | null>(null);
  protected changeStatus = signal<boolean>(false);
  protected validStatuses: string[] = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];
  protected selectedLevel!: ILevel['payload'];
  protected pagination = signal({ page: 0, size: 10 });
  protected newStatus: string = '';
  protected readonly flashService = inject(FlashService);
  protected isEditing = signal<boolean>(false);
  protected schools = signal<ISchools | null>(null);
  protected status: string = '';
  protected editLevel = signal<boolean>(false);
  protected nameOfLevel!: string;
  protected levelCode!: string;
  protected levelSequence!: string;
  protected levelDescription!: string;
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected generateLevels = signal<boolean>(true);
  protected isGenerating = signal<boolean>(false);
  protected isLoading = signal<boolean>(false);

  protected generateLevelsFn(): void {
    if (!this.schoolId) {
      this.flashService.failure('School ID required');
      return;
    }

    this.isGenerating.set(true);

    const levelDefinitions = [
      {
        name: 'Creche',
        code: 'CRH01',
        description: 'Introduction to early learning and social development for children.',
      },
      {
        name: 'Nursery 1',
        code: 'NUR01',
        description: 'Foundation learning for toddlers focusing on basic literacy and numeracy.',
      },
      {
        name: 'Nursery 2',
        code: 'NUR02',
        description: 'Advanced nursery level building on foundational literacy and social skills.',
      },
      {
        name: 'Kindergarten 1',
        code: 'KG1',
        description: 'First kindergarten level introducing structured learning through play.',
      },
      {
        name: 'Kindergarten 2',
        code: 'KG2',
        description: 'Pre-primary level preparing learners for basic education.',
      },
      {
        name: 'Basic 1',
        code: 'BSC01',
        description: 'First grade of basic education introducing core subjects.',
      },
      {
        name: 'Basic 2',
        code: 'BSC02',
        description: 'Second grade of basic education expanding core subject knowledge.',
      },
      {
        name: 'Basic 3',
        code: 'BSC03',
        description: 'Third grade of basic education deepening foundational skills.',
      },
      {
        name: 'Basic 4',
        code: 'BSC04',
        description: 'Fourth grade of basic education strengthening core competencies.',
      },
      {
        name: 'Basic 5',
        code: 'BSC05',
        description: 'Fifth grade of basic education advancing subject proficiency.',
      },
      {
        name: 'Basic 6',
        code: 'BSC06',
        description: 'Sixth grade of basic education preparing for higher basic level.',
      },
      {
        name: 'Basic 7',
        code: 'BSC07',
        description: 'Seventh grade of basic education introducing more advanced subject areas.',
      },
      {
        name: 'Basic 8',
        code: 'BSC08',
        description: 'Eighth grade of basic education deepening advanced academic knowledge.',
      },
      {
        name: 'Basic 9',
        code: 'BSC09',
        description: 'Final grade of basic education preparing learners for higher levels.',
      },
    ];

    const createNext = (index: number): void => {
      if (index >= levelDefinitions.length) {
        this.isGenerating.set(false);
        this.generateLevels.set(false);
        this.flashService.success('All levels generated successfully.');
        this.getLevels();
        return;
      }

      const definition = levelDefinitions[index];

      const levelForm = {
        school_id: this.schoolId,
        name: definition.name,
        code: definition.code,
        sequence: index + 1,
        description: definition.description,
      };

      this.levelService.createLevel(`${ApiEndpoints.url}/level`, levelForm).subscribe({
        next: () => {
          createNext(index + 1);
        },
        error: (err) => {
          this.isGenerating.set(false);
          this.flashService.failure(
            err?.error?.message ?? `Error generating ${definition.name}. Try again.`,
          );
        },
      });
    };

    createNext(0);
  }

  protected selectLevel(action: string, payload: ILevel['payload']): void {
    if (action === 'status') {
      this.changeStatus.set(true);
    } else {
      this.editLevel.set(true);
    }

    this.selectedLevel = payload;
    this.nameOfLevel = this.selectedLevel.name;
    this.levelCode = this.selectedLevel.code;
    this.levelSequence = this.selectedLevel.sequence;
    this.levelDescription = this.selectedLevel.description;
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getLevels();
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected navigateToLevelDetail(id: string): void {
    this.router.navigate([`../level-details`], {
      relativeTo: this.route,
      queryParams: {
        id: id,
      },
    });
  }

  protected getLevels(filterType = this.currentFilterType, value = this.currentFilterValue): void {
    this.isFiltering.set(true);
    this.isLoading.set(false); //Needed for shimmer effect

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (res) => {
        this.levels.set(res);
        this.isFiltering.set(false);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Refresh the page');
      },
    });
  }

  protected editLevelFn(form: NgForm, action: 'status' | 'edit'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    let payload: Record<string, any> = {};

    if (action === 'status') {
      payload = {
        status: this.newStatus,
      };
    }

    if (action === 'edit') {
      const original = this.selectedLevel;

      if (this.nameOfLevel.trim() !== original.name) {
        payload['name'] = this.nameOfLevel.trim();
      }

      if (this.levelCode.trim() !== original.code) {
        payload['code'] = this.levelCode.trim();
      }

      if (Number(this.levelSequence) !== Number(original.sequence)) {
        payload['sequence'] = Number(this.levelSequence);
      }

      const currentDescription = this.levelDescription?.trim() || '';
      const originalDescription = original.description?.trim() || '';

      if (currentDescription !== originalDescription) {
        payload['description'] = currentDescription;
      }
    }

    if (!Object.keys(payload).length) {
      this.flashService.failure('No changes detected');
      return;
    }

    this.isEditing.set(true);

    this.levelService
      .editLevel(`${ApiEndpoints.url}/level/${this.selectedLevel.id}`, payload)
      .subscribe({
        next: () => {
          this.isEditing.set(false);
          this.editLevel.set(false);
          this.flashService.success('Successfully updated level details');
          this.getLevels();
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error updating level details');
        },
      });
  }
}
